import React, { useEffect, useRef } from "react";

import {
  faGoogle,
  faMicrosoft,
} from "@nnr/fontawesome-classic-brands/icons/classic/brands";

import { FeedbackButton } from "@/components/FeedbackButton/FeedbackButton";
import { SPLASH_CONTENT } from "@/components/SplashScreen/SPLASH_CONTENT";
import { SplashScreen } from "@/components/SplashScreen/SplashScreen";
import { Button } from "@/components/ui/button";
import { signIn, useAuthContext } from "@/context/auth-context";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useNavigate } from "@tanstack/react-router";
import { toast } from "sonner";
import { Column } from "../components/ui/flex";

const LoginPage: React.FC = () => {
  const { token } = useAuthContext();
  const navigate = useNavigate();
  const autoRedirect = useRef(true);

  const loginRedirect = () => {
    // `redirect` is defined in ui/src/hooks/use-redirection.ts
    const redirectEncoded = new URLSearchParams(window.location.search).get(
      "redirect",
    );
    // For now we use a hard refresh so that it falls into the
    // auth routing mechanism.
    if (!redirectEncoded) {
      navigate({ to: "/u/workspaces", replace: true });
      return;
    }

    const redirect = decodeURIComponent(redirectEncoded);
    if (redirect.startsWith("http")) {
      window.location.href = redirect;
      return;
    }

    navigate({ to: redirect, replace: true });
  };

  useEffect(() => {
    if (token && autoRedirect.current) {
      autoRedirect.current = false;
      loginRedirect();
    }
  }, [token]);

  const signInAndNavigate = (provider: "google" | "microsoft") => {
    autoRedirect.current = false;
    signIn(provider)
      .then(({ token }) => {
        if (token) {
          loginRedirect();
        }
      })
      .catch((error: unknown) => {
        if (
          typeof error === "object" &&
          error !== null &&
          "code" in error &&
          (error.code === "auth/popup-closed-by-user" ||
            error.code === "auth/cancelled-popup-request")
        ) {
          return;
        }

        toast.error(
          error instanceof Error
            ? error.message
            : "Sign in failed. Please try again.",
        );
      });
  };

  return (
    <>
      <SplashScreen splashContent={SPLASH_CONTENT}>
        <span className="text-2xl">Sign in to your account</span>

        <div className="min-h-8" />

        <Column align="start" className="gap-4">
          <Button
            variant="default"
            size="lg"
            onClick={() => signInAndNavigate("google")}
          >
            <FontAwesomeIcon icon={faGoogle} />
            Sign in with Google
          </Button>

          <Button
            variant="default"
            size="lg"
            onClick={() => signInAndNavigate("microsoft")}
          >
            <FontAwesomeIcon icon={faMicrosoft} />
            Sign in with Microsoft
          </Button>
        </Column>
      </SplashScreen>

      <FeedbackButton className="absolute right-2 bottom-2" />
    </>
  );
};

export default LoginPage;
